import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { RelatedPartyEntity, RelatedPartyOtherBoard, ReportData } from '../data/types';
import { Section, Row, Box, T } from '../primitives/Universal';
import { ShareholdingBlock } from './Shareholding';
import { fontFamily, typeScale } from '../data/designTokens';
import { fmtDate } from './formatUtils';
import { getEdgeTypeLabels, relationTypeMap } from '../../Dashboard/DashboardModules/RelatedParties/relatedParty_constants';

// ──────────────────────────────────────────────────────────────────────────────
// Structure & Relations — tabular layout per Figma 32:100440.
// 3 columns: NAME / RELATION / RELATION TO.
// Bucket header rows ("DIRECTORS · 4") span full width; primary entity rows are
// bold; linked-entity rows nest under their parent with an indent and muted tint.
// Below the table the existing Shareholding pattern is rendered inline (verbatim).
// ──────────────────────────────────────────────────────────────────────────────

type GroupKey = 'directors' | 'shareholders' | 'former' | 'holdings' | 'subsidiaries' | 'associates' | 'jointVentures' | 'sisterConcerns';

// Directors and Former Directors are rendered here AND per-director in the
// Management module (each director's "Other Directorships" list on their
// detail page) — that overlap is intentional: this table gives the flat
// bucket/count view the UI's Related Parties tab shows, Management gives the
// per-director drill-down. Labels come from `getEdgeTypeLabels` (the same
// helper the dashboard tab uses) so the two surfaces never drift apart.
const GROUPS: Array<{ key: GroupKey; edgeType: string; label: string; childRelation: string }> = [
    { key: 'directors', edgeType: 'DIRECTOR', label: getEdgeTypeLabels('DIRECTOR'), childRelation: 'Common Director' },
    { key: 'former', edgeType: 'FORMER_DIRECTOR', label: getEdgeTypeLabels('FORMER_DIRECTOR'), childRelation: 'Common Director' },
    { key: 'shareholders', edgeType: 'SHAREHOLDER', label: getEdgeTypeLabels('SHAREHOLDER'), childRelation: 'Common Shareholder' },
    { key: 'holdings', edgeType: 'HOLDING_COMPANY', label: getEdgeTypeLabels('HOLDING_COMPANY'), childRelation: 'Common Holding' },
    { key: 'subsidiaries', edgeType: 'SUBSIDIARY', label: getEdgeTypeLabels('SUBSIDIARY'), childRelation: 'Common Subsidiary' },
    { key: 'associates', edgeType: 'ASSOCIATE', label: getEdgeTypeLabels('ASSOCIATE'), childRelation: 'Common Associate' },
    { key: 'jointVentures', edgeType: 'JOINT_VENTURE', label: getEdgeTypeLabels('JOINT_VENTURE'), childRelation: 'Common JV' },
    { key: 'sisterConcerns', edgeType: 'SISTER_CONCERN', label: getEdgeTypeLabels('SISTER_CONCERN'), childRelation: 'Common Director' },
];

// Max linked-entity sub-rows shown per primary entity. Beyond this we render
// a "+N more not listed" trailer so a single dense entity can't push the
// relations table past react-pdf's yoga capacity (≈ 250 atomic rows per
// Section before layout state corrupts — skill rule 26).
export const MAX_LINKED_PER_ENTITY = 8;

// Polus surfaces some edge / role values in SCREAMING_SNAKE_CASE
// ("SISTER_CONCERN", "FORMER_DIRECTOR"). Convert to Title Case for the
// Relation column. Pass through anything that's already human-readable.
const titleCaseEnum = (raw: string): string => {
    const s = raw.trim();
    if (!s || !/^[A-Z][A-Z0-9_]*$/.test(s)) return s;
    return s
        .split('_')
        .filter(Boolean)
        .map((w) => w[0] + w.slice(1).toLowerCase())
        .join(' ');
};

// Builds the section subtitle: counts always shown, but the "linked entities
// nested below" clause only when at least one rendered entity actually has
// linked entities — otherwise the promise of nesting is false.
export const buildRelationsSubtitle = (totalParties: number, bucketCount: number, hasAnyLinkedEntities: boolean): string => {
    const base = `${totalParties} part${totalParties === 1 ? 'y' : 'ies'} across ${bucketCount} bucket${bucketCount === 1 ? '' : 's'}`;
    return hasAnyLinkedEntities ? `${base} · Direct connections with linked entities nested below` : base;
};

// Single source of truth for "which linked-entity rows does this primary
// entity render". Prefers `otherBoards` — it carries each linked entity's own
// edgeType + entityType so the sub-row's relation label can be resolved
// exactly (see resolveNestedRelationLabel) — falling back to `otherEntities`
// (name-only) when otherBoards is empty. Both EntityUnit (rendering) and
// hasAnyLinkedEntities (the subtitle's nesting-clause decision) call this so
// they can never disagree about whether an entity has linked rows.
export const linkedRowsForEntity = (entity: RelatedPartyEntity): Array<Pick<RelatedPartyOtherBoard, 'name' | 'edgeType' | 'entityType'>> =>
    entity.otherBoards?.length ? entity.otherBoards : (entity.otherEntities ?? []).map((name) => ({ name }));

// Decides whether any entity across the filled buckets has at least one linked
// entity, so the subtitle's nesting clause isn't a false promise. Extracted so
// this decision can be tested without mounting the section (precedent:
// resolveExistingAicaReportEntry in shell/reportLineage.ts).
export const hasAnyLinkedEntities = (filledGroups: Array<{ entities: RelatedPartyEntity[] }>): boolean =>
    filledGroups.some(({ entities }) => entities.some((e) => linkedRowsForEntity(e).length > 0));

// Resolves a nested sub-row's exact relation label the same way the UI does
// (`SecondDegreeRow.tsx`: `relationTypeMap[entityEntry.type]?.[row.type]?.[row.edgeType]`),
// falling back to the group's generic `childRelation` when the combination
// isn't mapped, or when either type/edgeType is missing. Extracted as a pure
// helper (same pattern as `hasAnyLinkedEntities`) so it's testable without
// mounting.
export const resolveNestedRelationLabel = (
    parentEntityType: string | undefined,
    childEntityType: string | undefined,
    edgeType: string | undefined,
    fallback: string,
): string => {
    if (!parentEntityType || !childEntityType || !edgeType) return fallback;
    const label = (relationTypeMap as Record<string, Record<string, Record<string, string>>>)[parentEntityType]?.[childEntityType]?.[
        edgeType
    ];
    return label ?? fallback;
};

// Token palette for the table. Kept local — these values are tuned to the Figma spec.
const COLORS = {
    headerBorder: '#1F2937', // top + bottom border on the header row
    rowBorder: '#F0EFE9', // soft hairline between body rows
    bucketBg: '#FAFAF7', // soft tint for bucket header + nested rows
    nameStrong: '#1F2937', // bold primary name
    nameSoft: '#6B7280', // nested/muted name
    headText: '#6B7280', // header column labels
    relationInk: '#1F2937', // primary relation/role text
    relationSoft: '#6B7280', // nested relation text
};

// Column widths sum to ~100% of the inner section width. Numbers are flex-like proportions
// rather than fixed widths so the table fills the section in both PDF and screen contexts.
const COL_FLEX = { name: 50, relation: 22, relationTo: 28 } as const;

// ── Cell shells ──────────────────────────────────────────────────────────────

type CellProps = {
    flex: number;
    children?: React.ReactNode;
    paddingLeft?: number;
    bg?: string;
    bold?: boolean;
    muted?: boolean;
    /** Header-style: uppercase + tracking + smaller text */
    head?: boolean;
};

const Cell: React.FC<CellProps> = ({ flex, children, paddingLeft, bg, bold, muted, head }) => {
    return (
        <div
            style={{
                flex: `${flex} 1 0`,
                padding: '6px 10px',
                paddingLeft: paddingLeft != null ? 10 + paddingLeft : 10,
                background: bg,
                fontSize: head ? '0.66rem' : '0.78rem',
                color: muted ? COLORS.nameSoft : head ? COLORS.headText : COLORS.nameStrong,
                fontWeight: head ? 600 : bold ? 700 : 400,
                letterSpacing: head ? '0.06em' : 0,
                textTransform: head ? 'uppercase' : 'none',
                lineHeight: 1.4,
                wordBreak: 'break-word',
            }}
        >
            {children}
        </div>
    );
};

// ── Row variants ─────────────────────────────────────────────────────────────

const TableHeaderRow: React.FC = () => (
    <div style={{ borderTop: `1px solid ${COLORS.headerBorder}`, borderBottom: `1px solid ${COLORS.headerBorder}` }}>
        <div style={{ display: 'flex' }}>
            <Cell flex={COL_FLEX.name} head>
                Name
            </Cell>
            <Cell flex={COL_FLEX.relation} head>
                Relation
            </Cell>
            <Cell flex={COL_FLEX.relationTo} head>
                Relation To
            </Cell>
        </div>
    </div>
);

const BucketHeaderRow: React.FC<{ label: string; count: number }> = ({ label, count }) => (
    <div
        style={{
            background: COLORS.bucketBg,
            borderBottom: `1px solid ${COLORS.rowBorder}`,
            padding: '6px 10px',
            fontSize: '0.78rem',
            fontWeight: 700,
            color: COLORS.nameStrong,
            letterSpacing: '0.04em',
        }}
    >
        {label.toUpperCase()} · {count}
    </div>
);

const TableRow: React.FC<{
    name: string;
    relation: string;
    relationTo: string;
    bold?: boolean;
    muted?: boolean;
    bg?: string;
    indent?: number;
}> = ({ name, relation, relationTo, bold, muted, bg, indent }) => {
    return (
        <div style={{ display: 'flex', background: bg, borderBottom: `1px solid ${COLORS.rowBorder}` }}>
            <Cell flex={COL_FLEX.name} bg={bg} bold={bold} muted={muted} paddingLeft={indent}>
                {name}
            </Cell>
            <Cell flex={COL_FLEX.relation} bg={bg} muted={muted}>
                {relation}
            </Cell>
            <Cell flex={COL_FLEX.relationTo} bg={bg} muted={muted}>
                {relationTo}
            </Cell>
        </div>
    );
};

// One "entity unit" = primary row + its linked-entity sub-rows. Wrapped together so
// the primary doesn't orphan from its first linked entity at a page break — but if
// a unit has many sub-rows, we let it break naturally between sub-rows by NOT
// wrapping the whole unit (only the primary + first sub-row stay together).
// Exported so its exact rendered text can be asserted directly, without the
// heavier ReportData fixture StructureRelations needs.
export const EntityUnit: React.FC<{
    entity: RelatedPartyEntity;
    childRelation: string;
    primaryRelationTo: string;
}> = ({ entity, childRelation, primaryRelationTo }) => {
    // Polus stores shareholder stake in `edgeValue` as a bare number string
    // (e.g. "10" or "10.5"), which we then surface as `entity.designation`.
    // Rendered raw, the Relation column reads as a meaningless integer next
    // to a person's name. Append "%" when the value is a bare number; pass
    // through anything else (titles like "Director", already-formatted %, etc.).
    const rawDesignation = entity.designation || (entity.role ? entity.role : 'Director');
    const designation = /^\d+(\.\d+)?$/.test(rawDesignation.trim()) ? `${rawDesignation.trim()}%` : titleCaseEnum(rawDesignation);
    // Same helper the subtitle's `hasAnyLinkedEntities` check uses — keeps the
    // "does this entity have linked rows" decision and "what rows get
    // rendered" in agreement.
    const allLinked = linkedRowsForEntity(entity);
    // Cap sub-rows so a single dense entity (e.g. a director who sits on 30+
    // boards) can't bloat the relations table past react-pdf's yoga ceiling.
    // The overflow count gets surfaced as one trailing "+N more not listed"
    // row so the reader knows the list was truncated for layout.
    const linked = allLinked.slice(0, MAX_LINKED_PER_ENTITY);
    const overflowCount = Math.max(0, allLinked.length - linked.length);
    const primaryName = entity.name || '—';
    const PrimaryRow = <TableRow name={primaryName} relation={designation} relationTo={primaryRelationTo} bold />;
    // Per PM feedback: sub-rows should leave the Name column blank and put the
    // linked-entity name in the Relation To column. The director is already the
    // header of the group (PrimaryRow above), so repeating the director's name
    // in column 3 was redundant — the linked-entity name belongs there, which
    // also frees column 1 to act as visual indentation under the group header.
    const subRows = linked.map((other, i) => (
        <TableRow
            key={`${entity.name}-sub-${i}`}
            name=""
            relation={resolveNestedRelationLabel(entity.entityType, other.entityType, other.edgeType, childRelation)}
            relationTo={other.name}
            muted
            bg={COLORS.bucketBg}
            indent={20}
        />
    ));
    if (overflowCount > 0) {
        subRows.push(
            <TableRow
                key={`${entity.name}-overflow`}
                name=""
                relation=""
                relationTo={`+${overflowCount} more not listed`}
                muted
                bg={COLORS.bucketBg}
                indent={20}
            />,
        );
    }
    return (
        <>
            {PrimaryRow}
            {subRows}
        </>
    );
};

// ── Main module ──────────────────────────────────────────────────────────────

const StructureRelations: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const rp = data.relatedParties;
    const hasShareholding = !!data.shareholding && data.shareholding.rows.length > 0;

    const primaryRelationTo = data.identity?.orgName || '—';

    // Strip self-references — Polus occasionally returns the assessee company
    // itself as a related party (e.g. INTERGLOBE AVIATION LIMITED listed as
    // its own director under Shareholders bucket because the company holds
    // some of its own treasury stock). Surfacing this read as a data bug;
    // filter by normalized name match.
    const selfNameNorm = (data.identity?.orgName || '').toLowerCase().trim();
    const notSelf = (e: RelatedPartyEntity): boolean => {
        if (!selfNameNorm) return true;
        return (e.name || '').toLowerCase().trim() !== selfNameNorm;
    };

    // Total parties + bucket count for the section subtitle.
    let totalParties = 0;
    const filledGroups: Array<(typeof GROUPS)[number] & { entities: RelatedPartyEntity[] }> = [];
    GROUPS.forEach((g) => {
        const raw = (rp as any)?.[g.key] as RelatedPartyEntity[] | undefined;
        const list = raw?.filter(notSelf);
        if (list?.length) {
            totalParties += list.length;
            filledGroups.push({ ...g, entities: list });
        }
    });

    const hasRelations = filledGroups.length > 0;
    if (!hasRelations && !hasShareholding) return null;

    const anyLinkedEntities = hasAnyLinkedEntities(filledGroups);

    const subtitle = hasRelations ? buildRelationsSubtitle(totalParties, filledGroups.length, anyLinkedEntities) : 'Cap table snapshot';

    return (
        <Section kicker="Network" title="Structure & Relations" subtitle={subtitle} index={index} breakBefore={breakBefore}>
            {hasRelations ? (
                <>
                    <div style={{ marginTop: 8 }}>
                        <TableHeaderRow />
                        {filledGroups.map(({ key, label, childRelation, entities }) => (
                            <div key={key}>
                                <BucketHeaderRow label={label} count={entities.length} />
                                {entities.map((e, i) => (
                                    <EntityUnit
                                        key={`${e.name}-${i}`}
                                        entity={e}
                                        childRelation={childRelation}
                                        primaryRelationTo={primaryRelationTo}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            ) : null}

            {hasShareholding ? (
                // Shareholding pattern always starts on a fresh page in PDF —
                // sitting it at the bottom of the related-parties table reads
                // as a cramped afterthought, especially when the analyst
                // briefing block lands just above. Screen ignores `breakBefore`.
                <Box breakBefore marginTop={24}>
                    {/* skill rule 9 corollary: h3 + caption mixed sizes drift on baseline — center + lineHeight:1 */}
                    <Row align="center" justify="space-between" marginBottom={14}>
                        <T size="h3" family="bold" tone="ink00" lineHeight={1}>
                            Shareholding pattern
                        </T>
                        <T size="caption" tone="muted" lineHeight={1}>
                            As of {fmtDate(data.shareholding!.financialEndDate)}
                        </T>
                    </Row>
                    <ShareholdingBlock data={data} />
                </Box>
            ) : null}
        </Section>
    );
};

export const relatedPartiesModule: ModuleDefinition = {
    meta: {
        id: 'relatedParties',
        title: 'Structure & Relations',
        description: 'Directors, shareholders, holdings, linked entities, and shareholding pattern.',
        dataSource: 'Related Parties graph + Shareholding graph',
    },
    hasData: (d) => {
        const rp = d.relatedParties;
        const anyParty = !!rp && GROUPS.some(({ key }) => ((rp as any)[key] ?? []).length > 0);
        const anyShareholding = !!d.shareholding && d.shareholding.rows.length > 0;
        return anyParty || anyShareholding;
    },
    Screen: StructureRelations,
};
